/**
 * Resolve the API origin from the hostname a page is being served from.
 *
 * The API lives on its own host (`api.<env-domain>`) with routes mounted at
 * the root — there is no `/api` prefix in production or staging. Deriving the
 * origin from the current hostname instead of hardcoding it means one build
 * serves every domain we own, which is what lets the `.com` migration land
 * without a matching code change on every hostname.
 *
 * `www.` and `admin.` are stripped because both are siblings of the apex, not
 * separate environments: the admin SPA on `admin.pc.zarv.dev` must reach
 * `api.pc.zarv.dev`, not `api.admin.pc.zarv.dev`.
 *
 * This is also why every environment puts its site at an APEX rather than
 * under a label of its own: a `website.` prefix would survive the strip and
 * yield `api.website.<domain>`.
 *
 * Returns `null` for local development. Callers keep their own local branch
 * because they genuinely differ — the Next.js apps proxy a same-origin `/api`
 * path through a rewrite, while the Vite admin SPA calls the dev worker on
 * `http://localhost:8788` directly.
 *
 * Not idempotent: passing an API host back in prepends another `api.` label
 * instead of returning it unchanged (`api.podecontratar.com` →
 * `https://api.api.podecontratar.com`). No current call site passes an API
 * host — they all pass `window.location.hostname` or a request hostname —
 * but a future one must not either.
 *
 * Any host this function doesn't recognize still produces a syntactically
 * valid origin that doesn't exist: `podecontratar-admin-staging.pages.dev` →
 * `https://api.podecontratar-admin-staging.pages.dev`, which fails DNS. This
 * isn't a regression from this migration — a cookie scoped to
 * `.podecontratar.com` was never sent to `pages.dev` in the first place, so
 * the admin SPA was already unusable from a Pages preview alias — but
 * `scripts/deploy.sh` prints that alias URL, so someone will eventually land
 * on it and hit this.
 */
export function apiOriginForHost(hostname: string): string | null {
  if (hostname === "localhost" || hostname === "127.0.0.1") return null;
  return `https://api.${hostname.replace(/^(?:www|admin)\./, "")}`;
}

/**
 * Where the MCP worker listens in local development when nothing overrides it.
 * `wrangler dev` for `apps/workers/mcp` must be started on this port
 * (`--port 8789`); 8788 is the API worker.
 */
export const DEV_MCP_ORIGIN = "http://localhost:8789";

/**
 * Resolve the MCP worker's origin from the hostname a page is served from —
 * the same derivation as `apiOriginForHost`, with an `mcp.` label instead of
 * `api.`: `podecontratar.com` → `https://mcp.podecontratar.com`,
 * `pc.zarv.dev` → `https://mcp.pc.zarv.dev`. `www.`/`admin.` are stripped for
 * the same reason.
 *
 * Unlike the API there is no same-origin proxy for the MCP host in dev: the
 * consent page must POST a real top-level form to it and follow its redirect
 * back to the MCP client, so local development always gets an absolute
 * origin — `devOrigin` when given (the website passes
 * `NEXT_PUBLIC_MCP_ORIGIN`), else `DEV_MCP_ORIGIN`. The session cookie still
 * reaches it there, because cookies are not scoped by port.
 */
export function mcpOriginForHost(hostname: string, devOrigin?: string | null): string {
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return (devOrigin || DEV_MCP_ORIGIN).replace(/\/+$/, "");
  }
  return `https://mcp.${hostname.replace(/^(?:www|admin)\./, "")}`;
}

/**
 * The MCP worker's origin for the current page (browser only). `devOrigin`
 * overrides the local-development fallback; see `mcpOriginForHost`.
 *
 * Shared code cannot read `process.env` (this package has no Node types and
 * the admin SPA is Vite), so the website reads `NEXT_PUBLIC_MCP_ORIGIN` itself
 * and passes it in.
 */
export function mcpOrigin(devOrigin?: string | null): string {
  if (typeof window === "undefined") {
    throw new Error("mcpOrigin() is browser-only; use mcpOriginForHost(hostname) on the server");
  }
  return mcpOriginForHost(window.location.hostname, devOrigin);
}
